import { useEffect, useState } from "react";
import { useRouteContext } from "@tanstack/react-router";
import { useWatch, type UseFormReturn } from "react-hook-form";
import { Container } from "react-bootstrap";
import {
  FaCalendarCheck,
  FaTrashAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

import FormFieldRow from "@/components/shared/forms/FormFieldRow";
import ReadOnlySelect from "@/components/shared/forms/ReadOnlySelect";
import Select from "@/components/shared/forms/Select";
import TextInput from "@/components/shared/forms/TextInput";
import { type ATTN, type ENRL } from "@/models.ts";
import { useCoreData } from "@/queries.ts";
import {
  getSelectableAttendanceMonths,
  useATTNFormLogic,
  validateDaysExpectedSum,
} from "../../useATTNFormLogic";

type PreschoolSummary = {
  PreschoolCDSCode: string;
  PreschoolName: string;
};

type ATTNWizardFormProps = {
  form: UseFormReturn<ATTN>;
  preschools: PreschoolSummary[];
  enrlRecords: ENRL[];
  siblingAttnRecords: ATTN[];
  attendanceNumber?: number;
  onRemove?: () => void;
};

export function ATTNWizardForm({
  form,
  preschools,
  enrlRecords,
  siblingAttnRecords,
  attendanceNumber = 1,
  onRemove,
}: ATTNWizardFormProps) {
  const coreData = useCoreData();
  const leaContext = useRouteContext({ from: "/$leaid" });
  const tooltipMap = leaContext.coreData.tooltipMap.get("ATTN");

  const {
    register,
    control,
    formState: { errors },
  } = form;

  const watchCSPPID = useWatch({ control, name: "CSPPID" });
  const watchLocalChildId = useWatch({ control, name: "LocalChildId" });
  const watchProgramYear = useWatch({ control, name: "ProgramYear" });
  const watchPreschoolCode = useWatch({ control, name: "PreschoolCode" });
  const watchFirstOfMonth = useWatch({ control, name: "FirstOfMonth" });

  const { validators } = useATTNFormLogic(watchFirstOfMonth);

  const monthOptions = getSelectableAttendanceMonths(
    watchProgramYear,
    watchPreschoolCode,
    enrlRecords,
    siblingAttnRecords
  );

  const [collapsed, setCollapsed] = useState(false);

  const hasErrors = Object.keys(errors).length > 0;
  useEffect(() => {
    if (hasErrors) setCollapsed(false);
  }, [hasErrors]);

  const headerStyle = {
    backgroundColor: "#F9FAFB",
    padding: "14px 20px",
    cursor: "pointer",
    userSelect: "none" as const,
  };

  const Wrapper = attendanceNumber === 1 ? Container : "div";
  const wrapperProps =
    attendanceNumber === 1
      ? {
          className: "ps-3 pe-3 overview-container",
          style: { borderTop: "none", borderRadius: 0 } as React.CSSProperties,
        }
      : {
          className: "ps-3 pe-3",
        };

  return (
    <Wrapper {...wrapperProps}>
      <div
        className="form-card-modern mb-3"
        style={{
          overflow: "hidden",
          padding: 0,
          ...(onRemove
            ? { border: "2px dashed #c5ccd4", borderRadius: "8px" }
            : {}),
        }}
      >
        <div
          className="section-header d-flex justify-content-between align-items-center"
          style={{
            ...headerStyle,
            width: "100%",
            ...(collapsed ? { marginBottom: 0 } : {}),
          }}
        >
          <button
            type="button"
            className="d-flex align-items-center gap-2 flex-grow-1"
            style={{
              border: "none",
              background: "none",
              font: "inherit",
              textAlign: "left" as const,
              padding: 0,
            }}
            onClick={() => setCollapsed((prev) => !prev)}
            aria-expanded={!collapsed}
          >
            <FaCalendarCheck className="section-icon" />
            <h3 className="section-title mb-0">
              {attendanceNumber === 1
                ? "Attendance"
                : `Attendance ${attendanceNumber}`}
            </h3>
          </button>
          <div className="d-flex align-items-center gap-2">
            {onRemove && (
              <button
                type="button"
                className="btn btn-sm d-flex align-items-center gap-1"
                style={{
                  color: "#dc3545",
                  border: "none",
                  background: "transparent",
                  fontWeight: 500,
                  fontSize: "13px",
                }}
                onClick={onRemove}
              >
                <FaTrashAlt style={{ fontSize: "12px" }} />
                Remove
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm d-flex align-items-center"
              style={{ border: "none", background: "transparent", padding: 0 }}
              onClick={() => setCollapsed((prev) => !prev)}
              aria-expanded={!collapsed}
              aria-label={collapsed ? "Expand section" : "Collapse section"}
            >
              {collapsed ? (
                <FaChevronDown style={{ fontSize: "12px", color: "#6c757d" }} />
              ) : (
                <FaChevronUp style={{ fontSize: "12px", color: "#6c757d" }} />
              )}
            </button>
          </div>
        </div>

        {!collapsed && (
          <div style={{ padding: "20px" }}>
            <div className="row g-3">
              <div className="col-md-4">
                <FormFieldRow
                  name="CSPPID"
                  labelText="CSPPID"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                >
                  {(id, name) => (
                    <TextInput
                      id={id}
                      readOnly
                      value={watchCSPPID}
                      {...register(name)}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-4">
                <FormFieldRow
                  name="ProgramYear"
                  labelText="Program Year"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id, name) => (
                    <ReadOnlySelect
                      id={id}
                      control={control}
                      name={name}
                      rules={{ required: "Program Year is required" }}
                      options={coreData.programYearList}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-4">
                <FormFieldRow
                  name="PreschoolCode"
                  labelText="Preschool"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id, name) => (
                    <ReadOnlySelect
                      id={id}
                      control={control}
                      name={name}
                      rules={{ required: "Preschool is required" }}
                      options={preschools.map((x) => ({
                        Text: x.PreschoolName,
                        Value: x.PreschoolCDSCode,
                      }))}
                    />
                  )}
                </FormFieldRow>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <FormFieldRow
                  name="LocalChildId"
                  labelText="Local Child ID (CICN)"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id, name) => (
                    <TextInput
                      id={id}
                      readOnly
                      value={watchLocalChildId}
                      {...register(name, {
                        required: "Local Child ID (CICN) is required",
                      })}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-6">
                <FormFieldRow
                  name="FirstOfMonth"
                  labelText="Attendance Month"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id, name) => (
                    <Select
                      id={id}
                      {...register(name, {
                        required: "Attendance Month is required",
                      })}
                      options={monthOptions}
                    />
                  )}
                </FormFieldRow>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-md-4">
                <FormFieldRow
                  name="DaysExpected"
                  labelText="Days Expected"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id, name) => (
                    <TextInput
                      id={id}
                      type="number"
                      {...register(name, {
                        required: "Days Expected is required",
                        validate: (value) => {
                          const integerCheck =
                            validators.validateDaysExpected.integer(value);
                          if (integerCheck !== true) return integerCheck;

                          const maxCheck =
                            validators.validateDaysExpected.max(value);
                          if (maxCheck !== true) return maxCheck;

                          return validateDaysExpectedSum(form.getValues());
                        },
                      })}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-4">
                <FormFieldRow
                  name="DaysAttended"
                  labelText="Days Attended"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id, name) => (
                    <TextInput
                      id={id}
                      type="number"
                      {...register(name, {
                        required: "Days Attended is required",
                        validate: validators.validateNonNegativeInteger,
                      })}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-4">
                <FormFieldRow
                  name="DaysAbsentExcused"
                  labelText="Days Absent (Excused)"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                >
                  {(id, name) => (
                    <TextInput
                      id={id}
                      type="number"
                      {...register(name, {
                        validate: validators.validateOptionalNonNegativeInteger,
                      })}
                    />
                  )}
                </FormFieldRow>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-md-4">
                <FormFieldRow
                  name="DaysAbsentUnexcused"
                  labelText="Days Absent (Unexcused)"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                >
                  {(id, name) => (
                    <TextInput
                      id={id}
                      type="number"
                      {...register(name, {
                        validate: validators.validateOptionalNonNegativeInteger,
                      })}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-4">
                <FormFieldRow
                  name="DaysAbsentSuspension"
                  labelText="Days Absent (Suspension)"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                >
                  {(id, name) => (
                    <TextInput
                      id={id}
                      type="number"
                      {...register(name, {
                        validate: validators.validateOptionalNonNegativeInteger,
                      })}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-4">
                <FormFieldRow
                  name="DaysAttendedSuspension"
                  labelText="Days Attended (Suspension)"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                >
                  {(id, name) => (
                    <TextInput
                      id={id}
                      type="number"
                      {...register(name, {
                        validate: validators.validateOptionalNonNegativeInteger,
                      })}
                    />
                  )}
                </FormFieldRow>
              </div>
            </div>
          </div>
        )}
      </div>
    </Wrapper>
  );
}
